import { PlusOutlined } from "@ant-design/icons";
import {
    Button,
    Col,
    DatePicker, Empty,
    Form,
    Input,
    Modal,
    notification,
    Pagination,
    Popconfirm,
    Row,
    Space,
    Table,
} from "antd";
import dayjs from 'dayjs';
import { useEffect, useState } from "react";

import {
    addLease,
    deleteLease, getLeaseDetail,
    getLeaseList,
    sendLeaseSMS
} from "../../axios/api.lease";

import { useTranslation } from "react-i18next";

const { Column } = Table;
const { Item } = Form
interface propsModel {
  id: string;
  platform: string;
}

export const LeaseListPage = (props: propsModel) => {
    const { id, platform } = props;
    const [data, setData] = useState<any[]>();
    const [pageIndex, setPageIndex] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [search, setSearch] = useState<string>("");
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [formRef] = Form.useForm();
    const [leaseStartTime, setLeaseStartTime] = useState<any>();
    const [leaseEndTime, setLeaseEndTime] = useState<any>();
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [tempLeaseId, setTempLeaseId] = useState<string>("")
    const formatDate = "YYYY/MM/DD";

    const { t } = useTranslation();

    useEffect(() => { if (id) { getData() } }, []);
  
    const getData = (index=pageIndex, size=pageSize) => {
        getLeaseList(id, {pageIndex: index, pageSize: size, search: search}).then(
            (res) => {
                setPageIndex(res.pageIndex);
                setPageSize(res.pageSize);
                setData([...res.dataList]);
                setTotalCount(res.totalCount);
            },
            () => { notification.error({message: t("获取租赁信息失败")}) }
        );
    };

    const updateLeaseInfo = () => {
        formRef.validateFields().then(
            res => {                
                if (!res.leaseDate[0] || !res.leaseDate[1]) {
                    return notification.error({message: t("租赁时间不能为空")})
                }   
                addLease(id, {
                    id: tempLeaseId,
                    name: res.name,
                    phoneNumber: res.phoneNumber,
                    idNumber: res.idNumber,
                    start: res.leaseDate[0].format(),
                    end: res.leaseDate[1].format()
                }).then(() => {
                    // 刷新页面
                    getData();
                    setIsModalVisible(false);
                    setIsDisabled(true);
                    setTempLeaseId("");
                })
            }
        )    
    };

    const deleteLeaseInfo = (leaseId: string) => {
        deleteLease(id, leaseId).then(() => {getData()});
    };

    const editLeaseInfo = (leaseId: string) => {
        setIsModalVisible(true);
        getLeaseDetail(id, leaseId).then(
            res => {
                formRef.setFieldsValue({
                    name: res.name,
                    idNumber: res.idNumber,
                    phoneNumber: res.phoneNumber,
                    leaseDate: [dayjs(res.start, formatDate), dayjs(res.end, formatDate)]
                }); 
                setTempLeaseId(leaseId);
            },
            () => { notification.error({message: t("获取租赁详情失败")}) }
        )        
    };

    const clearModal = () => {
        setIsModalVisible(false);
        setIsDisabled(true);
        setLeaseStartTime("");
        setLeaseEndTime("");
        formRef.resetFields();
    };

    const onSearch = () => {
        getData(1);
    };

    const sendSMS = (leaseId: string) => {
        sendLeaseSMS(id, leaseId).then(
            () => { notification.success({message: t("短信发送成功,请注意查收!")}) },
            () => { notification.error({message: t("短信发送失败!")}) }
        )
    }
    
    return (<>
        <Row className="mb-2" style={{float: "right"}}>
            <Col>
                <Input
                    placeholder={t("搜索租借人/手机号")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onPressEnter={onSearch}
                ></Input>
            </Col>
            <Col>
                <Button type="primary" onClick={() => {onSearch()}}>{t("搜索")}</Button>
            </Col>
            <Col style={{marginLeft: "10px"}}>
                <Button type="primary" onClick={() => {setIsModalVisible(true)}}>
                    <PlusOutlined />{t("添加")}
                </Button>
            </Col>
        </Row>
        <Table 
            dataSource={data} 
            rowKey={record => record.id} 
            pagination={false} 
            size="small"
            locale={{emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("暂无数据")}></Empty>}}
        >
            <Column title={t("租借人")} key="name" dataIndex="name"></Column>
            <Column title={t("手机号")} key="phoneNumber" dataIndex="phoneNumber"></Column>
            <Column title={t("租借时间")} render={(item) => {
                return `${item.start}~${item.end}`
            }}></Column>
            <Column title={t("状态")} key="status" dataIndex="status" 
                render={(v) => v === "Ended" ? t("已结束") : v === "Started" ? t("进行中") : v === "NotStarted" ? t("未开始") : t("未定义")}
            ></Column>
            <Column title={t("操作")} key="id" dataIndex="id"
                render={(id, item: any) => {
                    return (
                        <Space size={8}>
                            <Button onClick={() => {editLeaseInfo(id)}} type="link" block>{t("编辑")}</Button>
                            <Popconfirm 
                                title={`${t("确认删除")}？`} 
                                onConfirm={() => {deleteLeaseInfo(id)}}
                                okText={t("确认")}
                                cancelText={t("取消")}
                            >
                                <Button type="link" block>{t("删除")}</Button>
                            </Popconfirm>
                            {item.status !== "Ended" && (
                                <Popconfirm 
                                    title={`${t("确认发送")}？`} 
                                    onConfirm={() => {sendSMS(id)}}
                                    okText={t("确认")}
                                    cancelText={t("取消")}
                                >
                                    <Button type="link" block>{t("发送短信")}</Button>
                                </Popconfirm>
                            )}
                        </Space>
                    )
                }}
            ></Column>
        </Table>
        <Pagination
            style={{ textAlign: "right", marginTop: "20px" }}
            pageSize={pageSize}
            current={pageIndex}
            total={totalCount}
            showTotal={(total, range) => `${range[0]}-${range[1]} / ${total} ${t("条")}`}
            onChange={(page, pageSize) => { getData(page, pageSize);}}
            onShowSizeChange={(current, size) => getData(current, size)}
        ></Pagination>
        <Modal
            confirmLoading={loading}
            maskClosable={false}
            open={isModalVisible}
            destroyOnClose={true}
            title={t("租赁信息")}
            width={"650px"}
            okText={t("确认")}
            cancelText={t("取消")}
            onOk={() => {updateLeaseInfo()}}
            onCancel={() => {clearModal()}}
            okButtonProps={{disabled: isDisabled}}
        >
            <Form
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 16 }}
                preserve={false}
                form={formRef}
                layout="horizontal"
                onValuesChange={() => {setIsDisabled(false)}}
                initialValues={{ 
                    name: "", 
                    idNumber: "", 
                    phoneNumber: "", 
                    leaseDate: [leaseStartTime, leaseEndTime] 
                }}
            >
                <Item label={t("租赁人")} name="name" rules={[{ required: true }]}>
                    <Input></Input>
                </Item>
                <Item label={t("租赁人证件号")} name="idNumber" rules={[{ required: true }]}>
                    <Input></Input>
                </Item>
                <Item label={t("手机号")} name="phoneNumber" rules={[{ required: true }]}>
                    <Input></Input>
                </Item>
                <Item label={t("租赁时间")} name="leaseDate" rules={[{ required: true }]}>
                    <DatePicker.RangePicker 
                        style={{width: "100%"}}
                        format={formatDate}
                    />
                </Item>
            </Form>
        </Modal>
    </>);
};
